/**
 * SOLID Review
 * S — Good. One concern: the game screen's ad config — asked for once per free player, around the
 *     Domain's rule for when to ask and what to keep.
 * O — Good. A new ad act is one more shell of the same shape.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Exactly the ad op of `ClientUseCases`.
 * D — Strong. Two port interfaces and the shared steps, all injected.
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { ICommandAdConfigLoaded } from '../../domain/ports/schemas/AdsMoves';
import type { IPortClientAdsServer } from '../ports/contracts';
import { DONE, type ClientSteps, type TOutcome } from '../shared/ClientSteps';

export interface IConfigDepAdsContext {
  readonly decisions: IPortClientDecisions;
  readonly ads: IPortClientAdsServer;
  /** The one serial section, shared with every other context. */
  readonly steps: ClientSteps;
}

const NOTHING: ICommandNothing = {};

/**
 * THE AD SLOT'S SHELL (frontend strict-ports plan, FE4d, 2026-10-04) — AppRoot's `useAdProviderConfig`,
 * moved: plan (whether to ask, marked asked), the server's A/B answer outside the serial section, then keep
 * it. A failed request keeps the defaults, as the hook's `.catch(() => {})` did, and is reported.
 */
export class AdsContext {
  constructor(private readonly deps: IConfigDepAdsContext) {}

  async loadAdConfig(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, steps, ads } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planAdConfig(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const { request } = plan.value;
    if (request === null) return DONE;
    const answer = await ads.adConfig(ctx, request);
    if (!answer.success) return answer;
    const loaded: ICommandAdConfigLoaded = { playerId: request.playerId, config: answer.value.config };
    const kept = await steps.session(ctx, (input) => decisions.adConfigLoaded(ctx, input, loaded));
    return kept.success ? DONE : kept;
  }
}
